import { useEffect, useRef, useState } from "react";
import {
  FaBars,
  FaArrowRight,
  FaBookOpen,
  FaChevronDown,
  FaGraduationCap,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

import logo from "../../../assets/images/logo colegio (32).png";
import "./SiteShell.css";

const MENU_GROUPS = [
  {
    title: "Institución",
    icon: FaBookOpen,
    links: [
      ["Inicio", "/"],
      ["Home", "/home"],
      ["Historia", "/historia"],
      ["Misión", "/mision"],
      ["Sedes y horarios", "/horariosSedes"],
      ["Equipo", "/Staff"],
    ],
  },
  {
    title: "Proyecto educativo",
    icon: FaGraduationCap,
    links: [
      ["Nivel Inicial", "/homeinicial"],
      ["Nivel Primario", "/nivelprimario"],
      ["Nivel Secundario", "/nivelsecundario"],
      ["Inglés intensivo", "/InglesIntensivo"],
    ],
  },
  {
    title: "Actividades",
    icon: FaUsers,
    links: [
      ["Educación física", "/deportes"],
      ["Arte", "/arteActividad"],
      ["Comunidad activa", "/actividades"],
      ["Catequesis", "/catequesis"],
      ["Equipos y casas", "/equipos"],
    ],
  },
  {
    title: "Información",
    icon: FaBookOpen,
    links: [
      ["Admisiones", "/nueva-admision"],
      ["Uniformes", "/Uniforms"],
      ["Trabajá con nosotros", "/trabajaConNosotros"],
      ["Contacto", "/contacto"],
      ["Login familias", "/login"],
      ["Login empleados", "/loginEmpleados"],
    ],
  },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const portalRef = useRef(null);
  const menuRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setPortalOpen(false);
  }, [location]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousFocus = document.activeElement;
    menuRef.current?.querySelector("button")?.focus();
    const trapFocus = (event) => {
      if (event.key !== "Tab") return;
      const items = menuRef.current.querySelectorAll("a[href], button");
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      previousFocus?.focus();
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setPortalOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!portalOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!portalRef.current?.contains(event.target)) setPortalOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [portalOpen]);

  useEffect(() => {
    document.body.classList.toggle("tc-menu-is-open", menuOpen);
    return () => document.body.classList.remove("tc-menu-is-open");
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header className="tc-site-header">
        <div className="tc-site-header-inner">
          <Link className="tc-site-brand" to="/" aria-label="Colegio Santo Tomás - Inicio">
            <img src={logo} alt="" />
            <span>
              <strong>Colegio Santo Tomás</strong>
              <small>Fundación educativa</small>
            </span>
          </Link>

          <nav className="tc-site-actions" aria-label="Accesos principales">
            <div className="tc-portal-menu" ref={portalRef}>
              <button
                type="button"
                className="tc-site-button tc-site-button-gold"
                aria-label="Accesos al portal"
                aria-expanded={portalOpen}
                onClick={() => setPortalOpen((open) => !open)}
              >
                Portal <FaChevronDown />
              </button>
              {portalOpen && (
                <div className="tc-portal-dropdown" role="menu">
                  <div className="tc-portal-dropdown-header">
                    <span>Accesos privados</span>
                    <strong>Elegí tu portal</strong>
                  </div>
                  <Link
                    className="tc-portal-access tc-portal-access-families"
                    to="/login"
                    onClick={() => setPortalOpen(false)}
                    role="menuitem"
                  >
                    <span className="tc-portal-access-icon"><FaUsers /></span>
                    <div className="tc-portal-access-copy">
                      <strong>Portal de familias</strong>
                      <small>Acceso para padres y tutores</small>
                    </div>
                    <FaArrowRight className="tc-portal-access-arrow" aria-hidden="true" />
                  </Link>
                  <Link
                    className="tc-portal-access tc-portal-access-employees"
                    to="/loginEmpleados"
                    onClick={() => setPortalOpen(false)}
                    role="menuitem"
                  >
                    <span className="tc-portal-access-icon"><FaBookOpen /></span>
                    <div className="tc-portal-access-copy">
                      <strong>Portal de empleados</strong>
                      <small>Acceso para docentes y personal</small>
                    </div>
                    <FaArrowRight className="tc-portal-access-arrow" aria-hidden="true" />
                  </Link>
                </div>
              )}
            </div>
            <button
              type="button"
              className="tc-site-button tc-site-button-light"
              aria-label="Abrir menú"
              aria-expanded={menuOpen}
              aria-controls="tc-main-menu"
              onClick={() => { setPortalOpen(false); setMenuOpen(true); }}
            >
              <FaBars /> Menú
            </button>
          </nav>
        </div>
      </header>

      <div ref={menuRef} role="dialog" aria-modal={menuOpen || undefined} aria-label="Explorá el colegio" id="tc-main-menu" className={`tc-mega-menu${menuOpen ? " is-open" : ""}`} aria-hidden={!menuOpen}>
        <div className="tc-mega-menu-top">
          <Link className="tc-site-brand" to="/" onClick={closeMenu}>
            <img src={logo} alt="" />
            <span><strong>Colegio Santo Tomás</strong><small>Fundación educativa</small></span>
          </Link>
          <button type="button" onClick={closeMenu} aria-label="Cerrar menú"><FaTimes /> Cerrar</button>
        </div>

        <div className="tc-mega-menu-content">
          <div className="tc-mega-menu-intro">
            <span>Explorá el colegio</span>
            <h2>Una comunidad para aprender y crecer</h2>
            <p>Conocé nuestra propuesta educativa, actividades y espacios para cada etapa.</p>
          </div>
          <div className="tc-mega-menu-grid">
            {MENU_GROUPS.map((group) => (
              <section key={group.title}>
                <h3><group.icon /> {group.title}</h3>
                <ul>
                  {group.links.map(([label, to]) => (
                    <li key={label}><Link to={to} onClick={closeMenu}>{label}</Link></li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>

        <div className="tc-mega-menu-footer">
          <span>Santa Rosa, La Pampa</span>
          <div>
            <a href="https://www.instagram.com/santotomassr/" target="_blank" rel="noopener noreferrer">Instagram</a>
            <a href="https://www.facebook.com/santotomasinstitucional" target="_blank" rel="noopener noreferrer">Facebook</a>
            <a href="https://wa.me/5492954313911" target="_blank" rel="noopener noreferrer">WhatsApp</a>
          </div>
        </div>
      </div>
    </>
  );
}
